/**
 * aggregateStatementReport.js
 *
 * Turn raw ledger rows → statement summary rows that look like:
 * [
 *   {
 *     SubTotalAmount:              "540.000000",
 *     TotalAmountWithDiscount:     "600.00",
 *     TotalAmountWithPrize:        "0.000000",
 *     TotalAmountWithoutDiscount:  "540.00",
 *     TotalTAmountWithoutPrize:    "0",
 *     TotalUnitWithDiscount:       "600.00",
 *     TotalWinAmountWithoutPrize:  "0",
 *     customer:                    "@BUY",
 *     discountAmount:              "60.00",
 *     termId:                      "38",
 *     termName:                    "13TH",
 *     userId:                      "28"
 *   },
 *   …
 * ]
 *
 * @param {Array<Object>} rows        – raw rows from the API
 * @param {string}        role        – "owner" | "agent" | "user" | …
 * @param {number|null}   currentUser – logged-in userId (needed for agent logic)
 * @returns {Array<Object>}           – aggregated & formatted summary
 */
function removeStartPrefix(input, prefix) {
    if(input==prefix) return input;
  if (input.startsWith(prefix)) {
    return input.slice(prefix.length);
  }
  return input;
}
export function aggregateStatementReport(rows, role = "user", currentUser = null,name = "",userAgentDiscount=false) {
  /* helper: cut trailing " - X" suffixes and handle nulls */
  const normalize = (val) => (val ?? "∅").split(" - ")[0];

  const bucket = {};

  rows.forEach((row) => {
    /* ── single-row calculations (mirror SQL) ─────────────────── */
    const digits   = (row.number ?? "").length;
    const is2D     = digits === 2;
    const is3D     = digits === 3;
    const isHit    = row.number === row.winNum;
    const isTHit   = is3D && Array.isArray(row.tNumbers) && row.tNumbers.includes(row.number);

    const rate     =
        is2D ? (userAgentDiscount?row.agentDisount2D ?? 0:row.discount2D ?? 0) / 100
      : is3D ? (userAgentDiscount?row.agentDisount3D ?? 0:row.discount3D ?? 0) / 100
      : 0;

    const amtValue     = row.amount * (row.tUnitPrice ?? 1);
    const discAmount   = amtValue * rate;
    const winPrize     = isHit  ? (is2D ? row.prize2D : row.prize3D) * row.amount * (row.tUnitPrice ?? 1) : 0;
    const tPrize       = isTHit ? (row.tPrize ?? 0) * row.amount * (row.tUnitPrice ?? 1)                : 0;
    const totalPrize   = winPrize + tPrize;

    const subTotal =
      ( row.amount
        - (is2D ? row.amount * (userAgentDiscount?row.agentDisount2D ?? 0:row.discount2D ?? 0) / 100
                : row.amount * (userAgentDiscount?row.agentDisount3D ?? 0:row.discount3D ?? 0) / 100)
        - (isHit  ? (is2D ? row.prize2D : row.prize3D) * row.amount : 0)
        - (isTHit ? (row.tPrize ?? 0) * row.amount                  : 0)
      ) * (row.tUnitPrice ?? 1);

    /* ── choose name vs customer depending on role ─────────────── */
    const displayName =
      role === "owner" || (role === "agent" && currentUser != row.userId)
        ? removeStartPrefix(normalize(row.name),name)
        : normalize(row.customer);

    const key = `${displayName}|${row.termId}|${row.userId}`;

    if (!bucket[key]) {
      bucket[key] = {
        customer: displayName,
        userId:   row.userId,
        termId:   row.termId,
        termName: row.termName,
        discountAmount:              0,
        totalAmountWithDiscount:     0,
        totalUnitWithDiscount:       0,
        totalAmountWithoutDiscount:  0,
        totalWinAmountWithoutPrize:  0,
        totalTAmountWithoutPrize:    0,
        totalAmountWithPrize:        0,
        subTotalAmount:              0
      };
    }

    const g = bucket[key];
    g.discountAmount              += discAmount;
    g.totalAmountWithDiscount     += amtValue;
    g.totalUnitWithDiscount       += row.amount;
    g.totalAmountWithoutDiscount  += amtValue - discAmount;
    g.totalWinAmountWithoutPrize  += isHit  ? row.amount : 0;
    g.totalTAmountWithoutPrize    += isTHit ? row.amount : 0;
    g.totalAmountWithPrize        += totalPrize;
    g.subTotalAmount              += subTotal;
  });

  /* ── final MySQL-style formatting & stable sort ──────────────── */
  return Object.values(bucket)
    .map((g) => ({
      SubTotalAmount:              g.subTotalAmount.toFixed(6),
      TotalAmountWithDiscount:     g.totalAmountWithDiscount.toFixed(2),
      TotalAmountWithPrize:        g.totalAmountWithPrize.toFixed(6),
      TotalAmountWithoutDiscount:  g.totalAmountWithoutDiscount.toFixed(2),
      TotalTAmountWithoutPrize:    String(Math.round(g.totalTAmountWithoutPrize)),
      TotalUnitWithDiscount:       g.totalUnitWithDiscount.toFixed(2),
      TotalWinAmountWithoutPrize:  String(Math.round(g.totalWinAmountWithoutPrize)),
      customer:                    g.customer,
      discountAmount:              g.discountAmount.toFixed(2),
      termId:                      String(g.termId),
      termName:                    g.termName,
      userId:                      String(g.userId)
    }))
    .sort((a, b) => a.customer.localeCompare(b.customer)
    );
}